import {LogOut, RefreshToken} from '../modules/auth/redux/AuthCRUD'

export class ApiResponse<T> {
  data: T
  constructor() {
    this.data = {} as T
    this.successful = false
    this.authenticated = false
    this.forbidden = false
  }

  static refreshTokenPromise: Promise<boolean | undefined> | undefined // this holds any in-progress token refresh requests

  static async Create<T = any>(action: () => Promise<Response>) {
    // Captured before the request is even sent, not just before this call's own RefreshToken()/
    // LogOut(). The guest portal fires several requests in parallel (ProjectGuestContext alone
    // starts three on mount), so once the guest's 30-minute cookie expires they typically 401
    // together — and whichever resolves first calls LogOut(), which does localStorage.clear()
    // and wipes this flag. Reading it after `await action()` meant every other in-flight call
    // would then see isProjectGuest as false and fall into the CRM-only branch below, reloading
    // the current (now-unauthenticated) guest URL instead of redirecting to the portal login.
    const isProjectGuest = localStorage.getItem('is-project-guest') === 'true'

    var response = await ApiResponse.CreateFromResponse<T>(await action())

    // 403 — a project guest hitting anything outside ProjectGuestRoutePolicy's allowlist gets a
    // 403, not a 401, so it would otherwise skip the teardown below and surface as a silently
    // empty result. Ordinary permission denials can't reach here (NotAuthorizedException maps
    // to 401 via CustomExceptionFilterAttribute), so the only 403s an authenticated guest can
    // receive — blocked route, suspended/expired account, cookie access-denied — all mean the
    // session doesn't belong there. Tear it down and send them back to the portal login.
    if (response.forbidden && isProjectGuest) {
      LogOut()
      window.location.href = '/projects/guest/login'
      return response
    }

    if (!response.authenticated) {
      // 401 — a project guest authenticates by cookie and never holds a refresh-token, so the
      // refresh below is a guaranteed no-op for them. Skip it and tear the session down here.
      if (isProjectGuest) {
        LogOut()
        window.location.href = '/projects/guest/login'
        return response
      }

      console.log('Attempting refresh')
      if (!ApiResponse.refreshTokenPromise) {
        console.log('Creating refresh')
        ApiResponse.refreshTokenPromise = RefreshToken()
      }

      var refreshResponse = await ApiResponse.refreshTokenPromise

      ApiResponse.refreshTokenPromise = undefined
      console.log(refreshResponse)

      if (refreshResponse) {
        console.log('Refresh complete')

        return await ApiResponse.CreateFromResponse<T>(await action())
      } else {
        console.log('Refresh failed')

        // CRM sessions only — project guests short-circuited above and never reach here.
        LogOut()
        window.location.reload()

        return response
      }
    } else {
      return response
    }
  }

  static async CreateFromResponse<T = any>(response: Response) {
    var result = new ApiResponse<T>()

    result.authenticated = true

    if (!response.ok) {
      result.successful = false
      if (response.status === 401) {
        result.authenticated = false
      } else {
        if (response.status === 403) result.forbidden = true

        try {
          var data = await response.json()
          result.data = data
          result.validationErrors = data.validationErrors
        } catch {
          try {
            var text = await response.text()
            result.data = text as any
          } catch {}
        }
      }
    } else {
      result.successful = true
      try {
        result.data = await response.json()
      } catch (ex) {
        console.log(ex)
        console.log('No content for this request')
      }
    }

    return result
  }

  successful: Boolean
  validationErrors: any
  authenticated: Boolean
  forbidden: Boolean
}

// Query values are URL-encoded: a raw "+" reads as a space server-side (so a search for
// "test+99@gmail.com" arrived as "test 99@gmail.com"), and a raw "&", "#" or "%" would cut the
// query short or corrupt it.
function encodeQueryValue(value: any) {
  return encodeURIComponent(value instanceof Date ? value.toISOString() : String(value))
}

export class ListQueryParams {
  constructor() {
    this.skip = 0
    this.take = 50
    this.direction = 'ascending'
    this.params = Array<QueryParameter>()
  }

  Sort(sort: string, direction?: string) {
    this.sort = sort

    if (direction && direction === 'desc') return this.Descending()
    if (direction && direction !== 'desc') return this.Ascending()

    return this
  }

  Descending() {
    this.direction = 'descending'
    return this
  }

  Ascending() {
    this.direction = 'ascending'
    return this
  }

  Paginate(skip: number, take: number) {
    this.skip = skip
    this.take = take
    return this
  }

  Search(value: string) {
    this.WithParam('q', value)
    return this
  }

  WithStart(value: any) {
    this.WithParam('start', value)
    return this
  }

  WithEnd(value: any) {
    this.WithParam('end', value)
    return this
  }

  WithParam(param: string, value: any) {
    this.params.push({
      key: param,
      value: value,
    })

    return this
  }

  skip: number
  take: number
  direction: string
  params: QueryParameter[]
  sort?: string

  GenerateQueryString() {
    var query = '?skip=' + this.skip + '&take=' + this.take
    if (this.sort) query = query + '&sort=' + this.sort + '&direction=' + this.direction

    if (this.params) {
      this.params.map((param) => {
        if (param.value && param.key) {
          query = query + '&' + param.key + '=' + encodeQueryValue(param.value)
        } else if (param.value !== null && param.value !== undefined) {
          query = query + '&' + param.key + '=' + encodeQueryValue(param.value)
        }
      })
    }

    return query
  }

  GenerateQueryStringWithoutPagination() {
    var queryParts: string[] = []

    if (this.sort) {
      queryParts.push('sort=' + this.sort)
      queryParts.push('direction=' + this.direction)
    }

    if (this.params) {
      this.params.forEach((param) => {
        if (param.value && param.key) {
          queryParts.push(param.key + '=' + encodeQueryValue(param.value))
        } else if (param.value !== null && param.value !== undefined) {
          queryParts.push(param.key + '=' + encodeQueryValue(param.value))
        }
      })
    }

    return queryParts.length > 0 ? '?' + queryParts.join('&') : ''
  }
}

export class QueryParameter {
  key?: string
  value?: any
}
